import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { format } from 'date-fns';

const { FiDownload, FiChevronUp, FiChevronDown, FiExternalLink, FiMapPin, FiActivity, FiCornerDownRight } = FiIcons;

const CalamityTable = ({ calamities, onCalamityClick }) => {
  const [sortField, setSortField] = useState('time_utc');
  const [sortDirection, setSortDirection] = useState('desc');
  const [expandedMainShocks, setExpandedMainShocks] = useState(new Set());

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedCalamities = [...calamities].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];

    if (sortField === 'time_utc') {
      aVal = new Date(aVal).getTime();
      bVal = new Date(bVal).getTime();
    } else if (sortField === 'nearest_city') {
      aVal = a.nearest_city?.name || 'zzzz'; // sort offshore to bottom
      bVal = b.nearest_city?.name || 'zzzz';
    } else if (sortField === 'magnitude') {
      aVal = a.magnitude || 0;
      bVal = b.magnitude || 0;
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const getAftershocksForMainShock = (mainShockId) => {
    return calamities
      .filter(c => c.aftershock_of === mainShockId)
      .sort((a, b) => new Date(a.time_utc) - new Date(b.time_utc));
  };

  const exportToCSV = () => {
    const headers = ['Time (PHT)', 'Type', 'Event', 'Magnitude/Intensity', 'Nearest City', 'Province', 'Coordinates', 'Alert', 'Source', 'Earthquake Type'];
    
    const allRows = [];
    const mainShocks = sortedCalamities.filter(c => c.earthquake_type !== 'aftershock');
    mainShocks.forEach(calamity => {
      allRows.push(calamity);
      if (calamity.earthquake_type === 'main_shock') {
        const aftershocks = getAftershocksForMainShock(calamity.id);
        allRows.push(...aftershocks);
      }
    });

    const csvContent = [
      headers.join(','),
      ...allRows.map(c => [
        format(new Date(c.time_utc), 'yyyy-MM-dd HH:mm:ss'),
        c.type,
        c.event_type?.replace('_', ' ') || 'N/A',
        c.magnitude ? c.magnitude.toFixed(1) : 'N/A',
        c.nearest_city?.name || 'Offshore',
        c.province,
        `"${c.lat.toFixed(3)},${c.lng.toFixed(3)}"`,
        c.alert,
        c.source.toUpperCase(),
        c.earthquake_type || 'N/A'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `philippines-calamities-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getCalamityIcon = (type, eventType, earthquakeType) => ({
    'earthquake': earthquakeType === 'aftershock' ? '📉' : (earthquakeType === 'main_shock' ? '💥' : '🌍'),
    'flood': '🌊',
    'tidal': eventType === 'high_tide' ? '⬆️' : '⬇️',
    'weather': ({
      'tropical_depression': '🌀',
      'monsoon': '🌧️'
    })[eventType] || '⛈️',
    'tsunami': '🌊',
    'volcanic': '🌋'
  })[type] || '⚠️';

  const getCalamityColor = (calamity) => {
    if (calamity.type === 'earthquake') {
      if (calamity.earthquake_type === 'aftershock') return 'text-gray-500 font-medium';
      if (calamity.magnitude >= 7) return 'text-red-600 font-bold';
      if (calamity.magnitude >= 6) return 'text-orange-600 font-semibold';
      if (calamity.magnitude >= 5) return 'text-yellow-600 font-medium';
    }
    return {
      'flood': 'text-blue-600 font-semibold',
      'tidal': 'text-cyan-600 font-medium',
      'weather': 'text-purple-600 font-semibold',
      'tsunami': 'text-red-600 font-bold',
      'volcanic': 'text-orange-600 font-semibold'
    }[calamity.type] || 'text-gray-600';
  };

  const getAlertBadge = (alert) => {
    const configs = {
      'Warning': 'bg-red-100 text-red-800',
      'Watch': 'bg-amber-100 text-amber-800',
      'Advisory': 'bg-blue-100 text-blue-800',
      'None': 'bg-gray-100 text-gray-600'
    };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${configs[alert] || configs['None']}`}>{alert}</span>;
  };

  const getRowBackgroundColor = (calamity) => {
    if (calamity.earthquake_type === 'aftershock') return 'bg-gray-50/50';
    const hoursDiff = (new Date() - new Date(calamity.time_utc)) / 36e5;
    if (calamity.alert === 'Warning' && hoursDiff < 6) return 'bg-red-50 border-l-4 border-red-500';
    if (calamity.alert === 'Watch' && hoursDiff < 12) return 'bg-amber-50 border-l-4 border-amber-500';
    if (hoursDiff < 2) return 'bg-gray-50';
    return '';
  };

  const toggleMainShockExpansion = (mainShockId) => {
    const newSet = new Set(expandedMainShocks);
    newSet.has(mainShockId) ? newSet.delete(mainShockId) : newSet.add(mainShockId);
    setExpandedMainShocks(newSet);
  };

  const SortButton = ({ field, children }) => (
    <button onClick={() => handleSort(field)} className="flex items-center space-x-1 text-left font-medium text-gray-900 hover:text-blue-600">
      <span>{children}</span>
      {sortField === field && <SafeIcon icon={sortDirection === 'asc' ? FiChevronUp : FiChevronDown} className="text-xs" />}
    </button>
  );

  const mainShocks = sortedCalamities.filter(c => c.earthquake_type !== 'aftershock');

  const CalamityRow = ({ calamity, isAftershock = false }) => {
    const aftershocks = !isAftershock && calamity.earthquake_type === 'main_shock' ? getAftershocksForMainShock(calamity.id) : [];
    const isExpanded = expandedMainShocks.has(calamity.id);

    return (
      <>
        <motion.tr
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`hover:bg-gray-100 transition-colors ${getRowBackgroundColor(calamity)} ${isAftershock ? 'text-sm' : ''}`}
        >
          <td className={`py-3 whitespace-nowrap ${isAftershock ? 'pl-8 pr-4' : 'px-4'}`}>
            <div className="flex items-center space-x-2">
              {isAftershock && <SafeIcon icon={FiCornerDownRight} className="text-gray-400" />}
              <span>{format(new Date(calamity.time_utc), 'MMM dd, HH:mm')}</span>
            </div>
          </td>
          <td className="px-4 py-3 whitespace-nowrap"><div className="flex items-center space-x-2"><span className="text-lg">{getCalamityIcon(calamity.type, calamity.event_type, calamity.earthquake_type)}</span><div><span className="capitalize">{calamity.type}</span>{calamity.event_type && <div className="text-xs text-gray-500">{calamity.event_type.replace('_', ' ')}</div>}</div></div></td>
          <td className="px-4 py-3 whitespace-nowrap"><span className={getCalamityColor(calamity)}>{calamity.type === 'earthquake' ? `M${calamity.magnitude.toFixed(1)}` : calamity.event_type?.replace('_', ' ') || 'Active'}</span>{calamity.tsunami?.has_warning && <span className="ml-2 text-red-500 text-xs">🌊</span>}</td>
          <td className="px-4 py-3 whitespace-nowrap">{calamity.nearest_city ? <div>{calamity.nearest_city.name} <div className="text-xs text-gray-500">{calamity.nearest_city.distance_km} km</div></div> : <span className="text-gray-500">Offshore</span>}</td>
          <td className="px-4 py-3 whitespace-nowrap">{calamity.province}</td>
          <td className="px-4 py-3 whitespace-nowrap">{getAlertBadge(calamity.alert)}</td>
          <td className="px-4 py-3 whitespace-nowrap uppercase">{calamity.source}</td>
          <td className="px-4 py-3 whitespace-nowrap"><div className="flex items-center space-x-2">
            {aftershocks.length > 0 && <button onClick={() => toggleMainShockExpansion(calamity.id)} className="text-purple-600 hover:text-purple-800 text-xs font-medium" title={`${isExpanded ? 'Hide' : 'Show'} ${aftershocks.length} aftershocks`}>{aftershocks.length} aftershocks</button>}
            <button onClick={() => onCalamityClick?.(calamity)} className="text-blue-600 hover:text-blue-800" title="Show on map"><SafeIcon icon={FiMapPin} /></button>
            {calamity.detail_url && <a href={calamity.detail_url} target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-gray-800" title="View details"><SafeIcon icon={FiExternalLink} /></a>}
          </div></td>
        </motion.tr>
        {isExpanded && aftershocks.map(aftershock => <CalamityRow key={aftershock.id} calamity={aftershock} isAftershock />)}
      </>
    );
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="p-4 border-b flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Calamity Feed</h2>
          <p className="text-sm text-gray-600">{calamities.length} events ({calamities.filter(c => c.earthquake_type ==='aftershock').length} aftershocks)</p>
        </div>
        <button onClick={exportToCSV} className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"><SafeIcon icon={FiDownload} /><span>Export CSV</span></button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50"><tr className="text-xs uppercase text-gray-500">
            <th className="px-4 py-3 font-medium text-left"><SortButton field="time_utc">Time (PHT)</SortButton></th>
            <th className="px-4 py-3 font-medium text-left">Type</th>
            <th className="px-4 py-3 font-medium text-left"><SortButton field="magnitude">Intensity</SortButton></th>
            <th className="px-4 py-3 font-medium text-left"><SortButton field="nearest_city">Nearest City</SortButton></th>
            <th className="px-4 py-3 font-medium text-left">Province</th>
            <th className="px-4 py-3 font-medium text-left">Alert</th>
            <th className="px-4 py-3 font-medium text-left">Source</th>
            <th className="px-4 py-3 font-medium text-left">Actions</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-200">
            {mainShocks.map(calamity => <CalamityRow key={calamity.id} calamity={calamity} />)}
          </tbody>
        </table>
      </div>
      {calamities.length === 0 && <div className="p-8 text-center text-gray-500"><SafeIcon icon={FiActivity} className="text-4xl mx-auto mb-2 opacity-50" /><p>No calamities found for the selected criteria</p></div>}
    </motion.div>
  );
};

export default CalamityTable;